import { useEffect, useState } from 'react'
import { Star, BadgeCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getProductReviews, type ProductReview } from '@/lib/api'
import { SkeletonGrid } from '@/components/ui/SkeletonGrid'

interface ReviewsSectionProps {
  slug: string
  rating: number
  reviewCount: number
}

export function ReviewsSection({ slug, rating, reviewCount }: ReviewsSectionProps) {
  const [reviews, setReviews] = useState<ProductReview[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getProductReviews(slug)
      .then((r) => {
        if (!cancelled) setReviews(r)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <div className="mt-10">
        <SkeletonGrid count={2} columns={2} aspectRatio="auto" />
      </div>
    )
  }

  if (reviews.length === 0) {
    return (
      <div className="mt-10 border-t border-ink/10 pt-10">
        <h3 className="mb-2 font-display text-2xl normal-case text-carbon">Reviews</h3>
        <p className="text-sm text-carbon/60">No reviews yet. Be the first to share your experience.</p>
      </div>
    )
  }

  return (
    <div className="mt-10 border-t border-ink/10 pt-10">
      <div className="mb-8 flex items-center gap-4">
        <h3 className="font-display text-2xl normal-case text-carbon">Reviews</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  'h-4 w-4',
                  i < Math.round(rating) ? 'fill-wd-orange text-wd-orange' : 'text-carbon/30'
                )}
              />
            ))}
          </div>
          <span className="text-sm text-carbon/60">
            {rating} · {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
          </span>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {reviews.map((review) => (
          <div key={review.id} className="bg-oyster p-6">
            <div className="mb-3 flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={cn(
                    'h-3 w-3',
                    i < review.rating ? 'fill-wd-orange text-wd-orange' : 'text-carbon/30'
                  )}
                />
              ))}
            </div>
            <h4 className="font-display text-lg normal-case text-carbon">{review.title}</h4>
            <p className="mt-2 text-sm leading-relaxed text-carbon/70">{review.body}</p>
            <div className="mt-4 flex items-center gap-2 text-xs text-carbon/50">
              <span>{review.author}</span>
              <span>·</span>
              <span>{review.date}</span>
              {review.verified && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 text-carbon">
                    <BadgeCheck className="h-3 w-3" /> Verified purchase
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
